import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { HttpService } from '@core/services';
import { PaceTaskHttpService } from '@core/services/http';
import { PaceTaskTabScreens } from '@models/enums';
import { PaceTask } from '@models/interfaces';
import { PaceTaskSelectors } from '@store/selectors';
import { prefetchedScreens } from '../../pace-task-prefetch-registry';

export interface UseAllPaceTasksResult {
  data: PaceTask[];
  isLoading: boolean;
  hasError: boolean;
  search: string;
  setSearch: (value: string) => void;
  invalidate: () => void;
}

export const useAllPaceTasks = (isActive: boolean): UseAllPaceTasksResult => {
  const paceTaskService = useMemo(
    () => HttpService.getHttpRequests(PaceTaskHttpService),
    [],
  );
  const data = useSelector(PaceTaskSelectors.getAllPaceTasks);

  const [search, setSearch] = useState<string>('');
  const [debouncedSearch, setDebouncedSearch] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasError, setHasError] = useState<boolean>(false);
  const lastFetchedSearch = useRef<string | null>(null);
  const prefetchedRef = useRef<boolean>(false);
  const [fetchTrigger, setFetchTrigger] = useState<number>(0);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);

    return (): void => clearTimeout(timer);
  }, [search]);

  const invalidate = useCallback((): void => {
    lastFetchedSearch.current = null;
    setFetchTrigger((t: number): number => t + 1);
  }, []);

  useEffect(() => {
    if (!isActive || lastFetchedSearch.current === debouncedSearch) {
      return;
    }

    lastFetchedSearch.current = debouncedSearch;
    setIsLoading(true);
    setHasError(false);

    paceTaskService
      .getAllPaceTasks(debouncedSearch)
      .then((): void => {
        setIsLoading(false);

        if (!prefetchedRef.current) {
          prefetchedRef.current = true;
          Object.values(PaceTaskTabScreens).forEach(
            (screen: PaceTaskTabScreens): void => {
              prefetchedScreens.add(screen);
              paceTaskService.getScreenPaceTasks(screen);
            },
          );
        }
      })
      .catch((): void => {
        setHasError(true);
        setIsLoading(false);
        lastFetchedSearch.current = null;
      });
  }, [isActive, debouncedSearch, fetchTrigger]);

  return { data, isLoading, hasError, search, setSearch, invalidate };
};
